/**
 * Serial Number Matching Utilities
 * Matches extracted serial numbers to equipment records
 */

/**
 * Extract serial number from filename using regex patterns
 * @param {string} filename - File name
 * @returns {Object|null} - Matched serial info or null
 */
export const extractSerialFromFilename = (filename) => {
  // Remove file extension
  const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
  
  const patterns = [
    // SN_12345, SN-12345, SN12345
    { regex: /SN[_-]?(\w+)/i, type: 'serial' },
    
    // Serial_12345, Serial-12345, Serial12345
    { regex: /Serial[_-]?(\w+)/i, type: 'serial' },
    
    // Asset_12345, AssetID_12345
    { regex: /Asset(?:\s*ID)?[_-]?(\w+)/i, type: 'asset' },
    
    // ID_12345
    { regex: /ID[_-]?(\w{3,})/i, type: 'id' },
    
    // Equipment_12345
    { regex: /Equipment[_-]?(\w+)/i, type: 'equipment' },
    
    // Alphanumeric codes (e.g., ECG001, ABC12345) - 2+ letters + 2+ digits (checked before numeric to capture full code)
    { regex: /([A-Z]{2,}\d{2,})/i, type: 'alphanumeric' },
    
    // Standalone 3+ digit numbers
    { regex: /(\d{3,})/, type: 'numeric' }
  ];
  
  for (const pattern of patterns) {
    const match = nameWithoutExt.match(pattern.regex);
    if (match) {
      return {
        value: match[1].trim(),
        type: pattern.type,
        source: 'filename',
        confidence: pattern.type === 'serial' || pattern.type === 'asset' ? 'high' : 'medium'
      };
    }
  }
  
  // If no pattern matched, try using the entire filename as a potential serial/asset ID
  // This handles cases where the file is named exactly as the serial number (e.g., "ABC12345.pdf")
  if (nameWithoutExt && nameWithoutExt.length >= 3) {
    return {
      value: nameWithoutExt.trim(),
      type: 'filename',
      source: 'filename',
      confidence: 'medium'
    };
  }
  
  return null;
};

/**
 * Create lookup index for faster equipment matching
 * @param {Array} assets - Equipment assets array
 * @returns {Object} - Lookup indexes
 */
export const createEquipmentIndex = (assets) => {
  const index = {
    bySerial: {},
    byAssetId: {},
    byEquipmentName: {},
    byDepartment: {}
  };
  
  assets.forEach(asset => {
    // Index by serial number (case-insensitive)
    if (asset.serialNo) {
      const key = String(asset.serialNo).toLowerCase().trim();
      index.bySerial[key] = asset;
    }
    
    // Index by asset ID (case-insensitive)
    if (asset.assetId) {
      const key = String(asset.assetId).toLowerCase().trim();
      index.byAssetId[key] = asset;
    }
    
    // Index by equipment name
    if (asset.equipmentName) {
      const key = String(asset.equipmentName).toLowerCase().trim();
      if (!index.byEquipmentName[key]) {
        index.byEquipmentName[key] = [];
      }
      index.byEquipmentName[key].push(asset);
    }
    
    // Index by department
    if (asset.department) {
      const key = String(asset.department).toLowerCase().trim();
      if (!index.byDepartment[key]) {
        index.byDepartment[key] = [];
      }
      index.byDepartment[key].push(asset);
    }
  });
  
  return index;
};

/**
 * Find matching equipment for a serial number
 * @param {string} serialNumber - Serial number to match
 * @param {Object} index - Equipment index from createEquipmentIndex
 * @param {Array} assets - Original assets array (for fuzzy matching)
 * @returns {Object} - Match result
 */
export const findMatchingEquipment = (serialNumber, index, assets) => {
  if (!serialNumber) {
    return { match: null, confidence: 'none', method: 'none' };
  }
  
  const normalizedSerial = String(serialNumber).toLowerCase().trim();
  
  // 1. Exact match on serial number
  if (index.bySerial[normalizedSerial]) {
    return {
      match: index.bySerial[normalizedSerial],
      confidence: 'high',
      method: 'serial_exact'
    };
  }
  
  // 2. Exact match on asset ID
  if (index.byAssetId[normalizedSerial]) {
    return {
      match: index.byAssetId[normalizedSerial],
      confidence: 'high',
      method: 'assetid_exact'
    };
  }
  
  // 3. Partial match on serial number
  for (const [key, asset] of Object.entries(index.bySerial)) {
    if (key.includes(normalizedSerial) || normalizedSerial.includes(key)) {
      return {
        match: asset,
        confidence: 'medium',
        method: 'serial_partial'
      };
    }
  }
  
  // 4. Partial match on asset ID
  for (const [key, asset] of Object.entries(index.byAssetId)) {
    if (key.includes(normalizedSerial) || normalizedSerial.includes(key)) {
      return {
        match: asset,
        confidence: 'medium',
        method: 'assetid_partial'
      };
    }
  }
  
  // 5. Fuzzy match on equipment name
  for (const asset of assets) {
    if (asset.equipmentName) {
      const name = String(asset.equipmentName).toLowerCase();
      if (name.includes(normalizedSerial) || normalizedSerial.includes(name)) {
        return {
          match: asset,
          confidence: 'low',
          method: 'name_fuzzy'
        };
      }
    }
  }
  
  return { match: null, confidence: 'none', method: 'none' };
};

/**
 * Search equipment by multiple criteria
 * @param {string} searchTerm - Search term
 * @param {Array} assets - Equipment assets
 * @returns {Array} - Matching equipment
 */
export const searchEquipment = (searchTerm, assets) => {
  if (!searchTerm || searchTerm.trim() === '') {
    return [];
  }
  
  const term = searchTerm.toLowerCase().trim();
  
  return assets.filter(asset => {
    const equipmentName = String(asset.equipmentName || '').toLowerCase();
    const assetId = String(asset.assetId || '').toLowerCase();
    const serialNo = String(asset.serialNo || '').toLowerCase();
    const department = String(asset.department || '').toLowerCase();
    const floor = String(asset.floor || '').toLowerCase();
    
    return equipmentName.includes(term) ||
           assetId.includes(term) ||
           serialNo.includes(term) ||
           department.includes(term) ||
           floor.includes(term);
  });
};

/**
 * Group equipment by criteria
 * @param {Array} assets - Equipment assets
 * @param {string} groupBy - Group by criteria (department, type, floor, equipmentName)
 * @returns {Object} - Grouped equipment
 */
export const groupEquipment = (assets, groupBy) => {
  const groups = {};
  
  assets.forEach(asset => {
    let key;
    switch (groupBy) {
      case 'department':
        key = asset.department || 'No Department';
        break;
      case 'type':
        key = asset.type || asset.equipmentName || 'Unknown Type';
        break;
      case 'floor':
        key = asset.floor || 'No Floor';
        break;
      case 'equipmentName':
        key = asset.equipmentName || 'Unknown Equipment';
        break;
      default:
        key = 'All';
    }
    
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(asset);
  });
  
  return groups;
};

/**
 * Get unique equipment names from assets
 * @param {Array} assets - Equipment assets
 * @returns {Array} - Unique equipment names sorted alphabetically
 */
export const getUniqueEquipmentNames = (assets) => {
  const names = new Set();
  assets.forEach(asset => {
    if (asset.equipmentName) {
      names.add(asset.equipmentName);
    }
  });
  return Array.from(names).sort();
};
